import {
  CrudLevelCount,
  NativePermissions,
  PermissionSource,
} from '@ariksa/inventory-core/api';

import { toTitleCase } from 'utils/string';

/**
 * One permission, as the positional tuple the API returns rather than an
 * object: the same four keys repeated over the thousands of permissions a
 * Read bucket holds was most of the response size. The generated client
 * types the tuple as Array<any>, so the positions are named here.
 *
 * `used` is null when the provider reports no usage at all, and `lastUsed` is
 * null whenever nothing dates the use -- a permission never used, or a source
 * that proves use without a timestamp.
 */
export type PermissionEntry = [
  permission: string,
  used: boolean | null,
  grantedBy: PermissionSource[],
  lastUsed: string | null,
];

export const permissionEntries = (
  group: NativePermissions,
): PermissionEntry[] => (group.permissions ?? []) as PermissionEntry[];

/**
 * Mirrors the inventory-core AgnosticCrudLevel contract. The generated client
 * no longer exports that enum, because the API returns crud levels as free
 * strings so a client can echo one back verbatim as select_crud_level.
 */
const CRUD_LEVEL_ORDER = [
  'create',
  'read',
  'update',
  'delete',
  'tagging',
  'usage',
  'delegation',
  'ownership',
  'permissions management',
];

export interface PrivilegeCount {
  // raw crud_level as returned by the API; echoed back as select_crud_level
  crudLevel: string;
  label: string;
  count: number;
  isHighPrivilege: boolean;
}

const orderIndex = (crudLevel: string) =>
  CRUD_LEVEL_ORDER.indexOf(crudLevel.toLowerCase());

/**
 * Order by the inventory-core AgnosticCrudLevel contract, appending any level
 * the contract does not know about alphabetically so new levels stay visible.
 */
export const orderPrivilegeCounts = (
  counts: PrivilegeCount[],
): PrivilegeCount[] =>
  [...counts].sort((a, b) => {
    const ai = orderIndex(a.crudLevel);
    const bi = orderIndex(b.crudLevel);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.label.localeCompare(b.label);
  });

/**
 * The API counts every level for the whole entity, already de-duplicated and
 * reflecting the same filters as the permissions it returns, so a level's
 * count always equals the number of permissions listed under it.
 */
export const getPrivilegeCounts = (
  counts: CrudLevelCount[] = [],
): PrivilegeCount[] =>
  orderPrivilegeCounts(
    counts
      .filter(count => !!count.crud_level && !!count.count)
      .map(count => ({
        crudLevel: count.crud_level,
        label: toTitleCase(count.crud_level),
        count: count.count,
        isHighPrivilege: !!count.high_privilege,
      })),
  );
